// SPDX-License-Identifier: MIT
// Parse the desktop discover output. The model (claude -p or a sovereign
// endpoint) returns the discovery JSON — discovery_summary + 3-5 questions —
// possibly fenced or wrapped in prose, since neither path gives an Anthropic
// structured-output guarantee. Mirrors the discover-attachments edge fn
// contract (supabase/functions/discover-attachments/lib.ts DISCOVERY_OUTPUT_SCHEMA).
import { extractJsonObject, ClaudeBridgeError } from './claude-output';
import type { DiscoveryQuestion } from '../projects/discover';

export interface DiscoverParsed {
  discovery_summary: string;
  questions: DiscoveryQuestion[];
}

export function parseDiscoverOutput(inner: string): DiscoverParsed {
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(extractJsonObject(inner)) as Record<string, unknown>;
  } catch (err) {
    throw err instanceof ClaudeBridgeError ? err : new ClaudeBridgeError('discover JSON malformed');
  }
  if (typeof parsed.discovery_summary !== 'string' || parsed.discovery_summary.trim().length === 0) {
    throw new ClaudeBridgeError('discover JSON missing discovery_summary');
  }
  if (!Array.isArray(parsed.questions)) {
    throw new ClaudeBridgeError('discover JSON missing questions array');
  }
  // Keep only well-formed questions ; an empty result after filtering is an error
  // (the prompt asks for 3-5, so zero usable means the model drifted).
  const questions: DiscoveryQuestion[] = (parsed.questions as unknown[])
    .filter((q): q is Record<string, unknown> => !!q && typeof q === 'object')
    .filter((q) => typeof q.id === 'string' && typeof q.question === 'string')
    .map((q) => ({
      id: q.id as string,
      question: q.question as string,
      ...(typeof q.placeholder === 'string' ? { placeholder: q.placeholder } : {}),
      ...(typeof q.source === 'string' ? { source: q.source as string } : {}),
      ...(typeof q.why === 'string' ? { why: q.why as string } : {}),
    }));
  if (questions.length === 0) {
    throw new ClaudeBridgeError('discover JSON has no usable questions');
  }
  return { discovery_summary: parsed.discovery_summary, questions };
}
